import { createBrowserRouter } from "react-router-dom"
import { lazy, Suspense } from "react"
import { isLoyaltyPointsEnabled } from "@/lib/feature-flags"
import Layout from "../components/Layout"
import ErrorPage from "../components/ErrorPage"
import RequireAuth, { RedirectIfAuthenticated } from "./_authenticated"

// Loading spinner for lazy-loaded routes
// eslint-disable-next-line react-refresh/only-export-components -- router config module, not a component module; helper coexists with the `router` export
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
)

// Lazy load all route components for code splitting
const RootIndex = lazy(() => import("./RootIndex"))
const EventDetail = lazy(() => import("./EventDetail"))
const Auth = lazy(() => import("./Auth"))
const CreateEvent = lazy(() => import("./CreateEvent"))
const Calendar = lazy(() => import("./Calendar"))
const OAuthCallback = lazy(() => import("./OAuthCallback"))
const Profile = lazy(() => import("./Profile"))
const ProfileEdit = lazy(() => import("./ProfileEdit"))
const ProfileNotifications = lazy(() => import("./ProfileNotifications"))
const ProfileUsername = lazy(() => import("./ProfileUsername"))
const Privacy = lazy(() => import("./Privacy"))
const Terms = lazy(() => import("./Terms"))
const Explore = lazy(() => import("./Explore"))
const ManageEvent = lazy(() => import("./ManageEvent"))
const MyEvents = lazy(() => import("./MyEvents"))
const TicketDetail = lazy(() => import("./TicketDetail"))
const EventSchedule = lazy(() => import("./EventSchedule"))
const EventCheckIn = lazy(() => import("./EventCheckIn"))
const CommunitySchedule = lazy(() => import("./CommunitySchedule"))
const CommunityEventCheckIn = lazy(() => import("./CommunityEventCheckIn"))
const CommunityTicketDetail = lazy(() => import("./CommunityTicketDetail"))
const CreateCommunityEvent = lazy(() => import("./CreateCommunityEvent"))
const EditCommunityEvent = lazy(() => import("./EditCommunityEvent"))
const Unsubscribe = lazy(() => import("./Unsubscribe"))
const InvitationAccept = lazy(() => import("./InvitationAccept"))
const GroupPage = lazy(() => import("./GroupPage"))
const GroupSettings = lazy(() => import("./GroupSettings"))
const CreateGroup = lazy(() => import("./CreateGroup"))
const MyGroups = lazy(() => import("./MyGroups"))
const AdminLayout = lazy(() => import("./AdminLayout"))
const AdminOverviewPage = lazy(() => import("./admin/AdminOverviewPage"))
const AdminUsersPage = lazy(() => import("./admin/AdminUsersPage"))
const AdminEventsPage = lazy(() => import("./admin/AdminEventsPage"))
const AdminPartnersPage = lazy(() => import("./admin/AdminPartnersPage"))
const AdminReportsPage = lazy(() => import("./admin/AdminReportsPage"))
const AdminAuditPage = lazy(() => import("./admin/AdminAuditPage"))
const PartnerDirectory = lazy(() => import("./PartnerDirectory"))
const PartnerDetail = lazy(() => import("./PartnerDetail"))
const PartnerRegistration = lazy(() => import("./PartnerRegistration"))
const PartnerDashboard = lazy(() => import("./PartnerDashboard"))
const PointsPage = lazy(() => import("./PointsPage"))
const RewardsPage = lazy(() => import("./RewardsPage"))
const LeaderboardPage = lazy(() => import("./LeaderboardPage"))
const IntegrationSettingsPage = lazy(() => import("./IntegrationSettings"))
const ClaimTicket = lazy(() => import("./ClaimTicket"))
const TapInMarketing = lazy(() => import("./TapInMarketing"))
const RoadmapMarketing = lazy(() => import("./RoadmapMarketing"))
const EcosystemMarketing = lazy(() => import("./EcosystemMarketing"))

// Wrapper component for lazy routes
// eslint-disable-next-line react-refresh/only-export-components -- router config module, not a component module; helper coexists with the `router` export
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
)

const loyaltyRoutes = isLoyaltyPointsEnabled()
  ? [
      {
        path: "points",
        element: <LazyRoute><RequireAuth><PointsPage /></RequireAuth></LazyRoute>,
      },
      {
        path: "rewards",
        element: <LazyRoute><RewardsPage /></LazyRoute>,
      },
      {
        path: "leaderboard",
        element: <LazyRoute><LeaderboardPage /></LazyRoute>,
      },
    ]
  : []

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <LazyRoute><RootIndex /></LazyRoute>,
      },
      {
        path: "event/:slug",
        element: <LazyRoute><EventDetail /></LazyRoute>,
      },
      {
        path: "event/:slug/schedule",
        element: <LazyRoute><EventSchedule /></LazyRoute>,
      },
      {
        path: "event/:slug/check-in",
        element: <LazyRoute><EventCheckIn /></LazyRoute>,
      },
      {
        path: "event/:slug/community",
        element: <LazyRoute><CommunitySchedule /></LazyRoute>,
      },
      {
        path: "event/:slug/community/create",
        element: <LazyRoute><RequireAuth><CreateCommunityEvent /></RequireAuth></LazyRoute>,
      },
      {
        path: "event/:slug/community/:communityEventId/edit",
        element: <LazyRoute><RequireAuth><EditCommunityEvent /></RequireAuth></LazyRoute>,
      },
      {
        path: "event/:slug/community/:communityEventId/check-in",
        element: <LazyRoute><CommunityEventCheckIn /></LazyRoute>,
      },
      {
        path: "create",
        element: <LazyRoute><RequireAuth><CreateEvent /></RequireAuth></LazyRoute>,
      },
      {
        path: "calendar",
        element: <LazyRoute><Calendar /></LazyRoute>,
      },
      {
        path: "my-events",
        element: <LazyRoute><RequireAuth><MyEvents /></RequireAuth></LazyRoute>,
      },
      {
        path: "profile",
        element: <LazyRoute><RequireAuth><Profile /></RequireAuth></LazyRoute>,
      },
      {
        path: "profile/edit",
        element: <LazyRoute><RequireAuth><ProfileEdit /></RequireAuth></LazyRoute>,
      },
      {
        path: "profile/notifications",
        element: <LazyRoute><RequireAuth><ProfileNotifications /></RequireAuth></LazyRoute>,
      },
      {
        path: "settings/integrations",
        element: <LazyRoute><RequireAuth><IntegrationSettingsPage /></RequireAuth></LazyRoute>,
      },
      {
        path: "profile/:username",
        element: <LazyRoute><ProfileUsername /></LazyRoute>,
      },
      {
        path: "auth",
        element: (
          <LazyRoute>
            <RedirectIfAuthenticated>
              <Auth />
            </RedirectIfAuthenticated>
          </LazyRoute>
        ),
      },
      {
        path: "privacy",
        element: <LazyRoute><Privacy /></LazyRoute>,
      },
      {
        path: "terms",
        element: <LazyRoute><Terms /></LazyRoute>,
      },
      {
        path: "unsubscribe/success",
        element: <LazyRoute><Unsubscribe /></LazyRoute>,
      },
      {
        path: "unsubscribe/invalid",
        element: <LazyRoute><Unsubscribe /></LazyRoute>,
      },
      {
        path: "invitation/:token",
        element: <LazyRoute><InvitationAccept /></LazyRoute>,
      },
      {
        path: "claim/:token",
        element: <LazyRoute><ClaimTicket /></LazyRoute>,
      },
      {
        path: "explore",
        element: <LazyRoute><Explore /></LazyRoute>,
      },
      {
        path: "tap-in",
        element: <LazyRoute><TapInMarketing /></LazyRoute>,
      },
      {
        path: "roadmap",
        element: <LazyRoute><RoadmapMarketing /></LazyRoute>,
      },
      {
        path: "ecosystem",
        element: <LazyRoute><EcosystemMarketing /></LazyRoute>,
      },
      {
        path: "group/:slug",
        element: <LazyRoute><GroupPage /></LazyRoute>,
      },
      {
        path: "group/:slug/settings",
        element: <LazyRoute><RequireAuth><GroupSettings /></RequireAuth></LazyRoute>,
      },
      {
        path: "groups/create",
        element: <LazyRoute><RequireAuth><CreateGroup /></RequireAuth></LazyRoute>,
      },
      {
        path: "groups",
        element: <LazyRoute><RequireAuth><MyGroups /></RequireAuth></LazyRoute>,
      },
      {
        path: "partners",
        element: <LazyRoute><PartnerDirectory /></LazyRoute>,
      },
      {
        path: "partners/register",
        element: <LazyRoute><PartnerRegistration /></LazyRoute>,
      },
      {
        path: "partners/:id",
        element: <LazyRoute><PartnerDetail /></LazyRoute>,
      },
      ...loyaltyRoutes,
      {
        path: "partner",
        element: <LazyRoute><RequireAuth><PartnerDashboard /></RequireAuth></LazyRoute>,
      },
      {
        path: "manage-event/:slug",
        element: <LazyRoute><RequireAuth><ManageEvent /></RequireAuth></LazyRoute>,
      },
      {
        path: "ticket/:id",
        element: <LazyRoute><RequireAuth><TicketDetail /></RequireAuth></LazyRoute>,
      },
      {
        path: "community-ticket/:id",
        element: <LazyRoute><RequireAuth><CommunityTicketDetail /></RequireAuth></LazyRoute>,
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <LazyRoute>
        <AdminLayout />
      </LazyRoute>
    ),
    children: [
      {
        index: true,
        element: <LazyRoute><AdminOverviewPage /></LazyRoute>,
      },
      {
        path: "users",
        element: <LazyRoute><AdminUsersPage /></LazyRoute>,
      },
      {
        path: "events",
        element: <LazyRoute><AdminEventsPage /></LazyRoute>,
      },
      {
        path: "partners",
        element: <LazyRoute><AdminPartnersPage /></LazyRoute>,
      },
      {
        path: "reports",
        element: <LazyRoute><AdminReportsPage /></LazyRoute>,
      },
      {
        path: "audit",
        element: <LazyRoute><AdminAuditPage /></LazyRoute>,
      },
    ],
  },
  // OAuth callback route (outside Layout - standalone page)
  {
    path: "/auth/callback",
    element: <LazyRoute><OAuthCallback /></LazyRoute>,
  },
])

